import type { DateRange, SojournDetailDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { formatRange } from '../../shared/format.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button, CollapsibleCard, ConfirmButton, Select } from '../../shared/ui/index.js';
import { useHouses } from '../houses/houseHooks.js';
import { DateRangeEditor } from './DateRangeEditor.js';
import { useRemoveSojournHouse, useSetSojournHouse } from './sojournHooks.js';

export function SojournHouses({ sojourn }: { sojourn: SojournDetailDto }): ReactElement {
  const t = useTranslation();
  const houses = useHouses();
  const setHouse = useSetSojournHouse(sojourn.id);
  const removeHouse = useRemoveSojournHouse(sojourn.id);
  const [editing, setEditing] = useState<string | null>(null);

  const taken = new Set(sojourn.houses.map((house) => house.houseId));
  const available = (houses.data ?? []).filter((house) => !taken.has(house.id));
  const bounds: DateRange = { start: sojourn.startDate, end: sojourn.endDate };

  return (
    <CollapsibleCard
      title={t.sojournHouses.title}
      subtitle={t.count.houses(sojourn.houses.length)}
      error={setHouse.error ?? removeHouse.error}
      actions={
        available.length > 0 ? (
          <Select
            aria-label={t.sojournHouses.addHouse}
            value=""
            onChange={(event) => {
              if (event.target.value !== '') {
                setHouse.mutate({ houseId: event.target.value });
              }
            }}
          >
            <option value="">{t.sojournHouses.addHousePlaceholder}</option>
            {available.map((house) => (
              <option key={house.id} value={house.id}>
                {house.name}
              </option>
            ))}
          </Select>
        ) : null
      }
    >
      {sojourn.houses.length === 0 ? (
        <p className="field__hint">{t.sojournHouses.emptyDescription}</p>
      ) : (
        <ul className="data-list">
          {sojourn.houses.map((house) => (
            <li key={house.houseId} className="list-row" style={{ alignItems: 'flex-start' }}>
              <div className="list-row__main" style={{ flex: '1 1 320px' }}>
                <span className="list-row__title">{house.houseName}</span>
                <span className="list-row__meta">
                  {t.count.rooms(house.roomCount)} · {t.count.beds(house.bedCount)}
                </span>

                {editing === house.houseId ? (
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <DateRangeEditor
                      ranges={house.availability}
                      bounds={bounds}
                      disabled={setHouse.isPending}
                      onChange={(availability) =>
                        setHouse.mutate({ houseId: house.houseId, availability })
                      }
                    />
                  </div>
                ) : (
                  <span className="list-row__meta">
                    {t.sojournHouses.availableRanges(
                      house.availability.map(formatRange).join(', '),
                    )}
                  </span>
                )}
              </div>

              <div className="row">
                <Button
                  small
                  variant="secondary"
                  onClick={() => setEditing(editing === house.houseId ? null : house.houseId)}
                >
                  {editing === house.houseId ? t.common.done : t.sojournHouses.availability}
                </Button>
                <ConfirmButton
                  small
                  label={t.common.remove}
                  onConfirm={() => removeHouse.mutate(house.houseId)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}
